/**
 * PhotoReview — Full-screen photo review with pin annotation overlay.
 *
 * After a photo is captured, the surveyor can review it and place pins
 * to mark deficiencies. Each pin gets a comment and optional severity.
 * Annotations are saved alongside the photo in IndexedDB.
 */

import { useState, useRef, useCallback } from 'react'
import { X, MapPin, Trash2, Save } from 'lucide-react'
import type { SeverityLevel } from '../../lib/services/survey'

export interface PhotoAnnotation {
  id: string
  /** Relative position (0-1) on the image */
  x: number
  y: number
  comment: string
  severity: SeverityLevel
  created_at: string
}

interface PhotoReviewProps {
  /** Object URL or blob URL for the photo */
  photoUrl: string
  /** Existing annotations (for editing) */
  annotations?: PhotoAnnotation[]
  /** When true, saving requires at least one pin with a comment. */
  requireCommentedPin?: boolean
  /** Called when user saves annotations */
  onSave: (annotations: PhotoAnnotation[]) => void
  /** Called when user closes without saving */
  /**
   * Fired when the user dismisses the overlay (X button). Receives the
   * current pin array so the parent can persist what the user has placed
   * even if they never tapped the explicit Save button. Prevents silent
   * pin loss when `requireCommentedPin` validation blocked an explicit
   * Save, or when the user tapped "Done" on a pin detail panel and then
   * dismissed assuming the pin was saved.
   */
  onClose: (pins: PhotoAnnotation[]) => void
}

function hasCommentedPin(pins: PhotoAnnotation[]): boolean {
  return pins.some((pin) => pin.comment.trim().length > 0)
}

export function PhotoReview({ photoUrl, annotations: initial = [], requireCommentedPin = false, onSave, onClose }: PhotoReviewProps) {
  const [pins, setPins] = useState<PhotoAnnotation[]>(initial)
  const [selectedPin, setSelectedPin] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [severity, setSeverity] = useState<SeverityLevel>(1)
  const [isPinMode, setIsPinMode] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const imgRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const resolvePinCoordinates = useCallback((clientX: number, clientY: number) => {
    const target = imageRef.current ?? imgRef.current
    if (!target) return null
    const rect = target.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return null
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      setValidationError('Tap directly on the image to place a pin.')
      return null
    }

    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    }
  }, [])

  const placePinAt = useCallback(
    (clientX: number, clientY: number) => {
      const coordinates = resolvePinCoordinates(clientX, clientY)
      if (!coordinates) return

      const newPin: PhotoAnnotation = {
        id: crypto.randomUUID(),
        x: coordinates.x,
        y: coordinates.y,
        comment: '',
        severity: 1,
        created_at: new Date().toISOString(),
      }
      setPins((prev) => [...prev, newPin])
      setSelectedPin(newPin.id)
      setComment('')
      setSeverity(1)
      setValidationError(null)
      setIsPinMode(false)
    },
    [resolvePinCoordinates],
  )

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      placePinAt(e.clientX, e.clientY)
    },
    [placePinAt],
  )

  const handleImageAreaClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPinMode) return
      placePinAt(e.clientX, e.clientY)
    },
    [isPinMode, placePinAt],
  )

  const updateSelectedPin = useCallback(() => {
    if (!selectedPin) return
    setPins((prev) =>
      prev.map((p) => (p.id === selectedPin ? { ...p, comment, severity } : p)),
    )
    setSelectedPin(null)
    setValidationError(null)
  }, [selectedPin, comment, severity])

  const deletePin = useCallback(
    (id: string) => {
      setPins((prev) => prev.filter((p) => p.id !== id))
      if (selectedPin === id) setSelectedPin(null)
    },
    [selectedPin],
  )

  // When selecting a pin, load its data into the form
  const selectPin = useCallback((pin: PhotoAnnotation) => {
    setSelectedPin(pin.id)
    setComment(pin.comment)
    setSeverity(pin.severity)
    setValidationError(null)
  }, [])

  /** Collapse any in-flight pin-detail edits into the pin list. */
  const currentPins = useCallback((): PhotoAnnotation[] => {
    return selectedPin
      ? pins.map((p) => (p.id === selectedPin ? { ...p, comment, severity } : p))
      : pins
  }, [comment, pins, selectedPin, severity])

  const savePins = useCallback(() => {
    const nextPins = currentPins()
    setPins(nextPins)

    if (requireCommentedPin && !hasCommentedPin(nextPins)) {
      setValidationError('Add at least one pin comment before saving.')
      return
    }

    onSave(nextPins)
  }, [currentPins, onSave, requireCommentedPin])

  const handleDismiss = useCallback(() => {
    // Pass the current pins back so the parent can persist them even if the
    // user never tapped the validated Save button. See onClose docstring.
    onClose(currentPins())
  }, [currentPins, onClose])

  const severityColors: Record<number, string> = {
    1: '#60a5fa', // blue
    2: '#f59e0b', // amber
    3: '#dc2626', // red
  }

  return (
    // Outer container is scrollable so the pin-detail panel (comment box +
    // severity + Done button) and the pin list remain reachable on small
    // mobile viewports where header + image + bottom panel exceeds viewport
    // height. Scroll here is safe now that the pin-placement surface is a
    // <button> overlay — iOS no longer routes taps into ancestor scroll.
    <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/50">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsPinMode(!isPinMode)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              isPinMode
                ? 'bg-amber-500 text-white'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            <MapPin size={14} />
            {isPinMode ? 'Tap image to place pin' : 'Add Pin'}
          </button>
          <span className="text-xs text-white/60">{pins.length} pin(s)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={savePins}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500 text-white"
          >
            <Save size={14} />
            Save
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close — keep placed pins"
            className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {validationError && (
        <div className="bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-900">
          {validationError}
        </div>
      )}

      {/* Image with pins.
          Pin-placement surface is a <button> overlay (shown only in pin mode)
          so iOS Safari fires click events natively. `<div onClick>` on iOS is
          flaky inside scrollable parents even with touch-manipulation and
          native touchend listeners; `<button>` bypasses the synthesized-click
          heuristics entirely. Mirrors the precedent in
          components/feedback/SmeFeedbackOverlay.tsx:101-120 which proved this
          pattern works reliably on real iOS. We keep the outer <div> so the
          existing pin-marker <button>s remain valid children (you can't nest
          buttons). */}
      <div
        className="flex-1 flex items-center justify-center p-2"
        data-testid="photo-review-placement-area"
        onClick={handleImageAreaClick}
      >
        <div
          ref={imgRef}
          className="relative max-w-full max-h-full select-none touch-manipulation"
        >
          <img
            ref={imageRef}
            src={photoUrl}
            alt="Photo under review"
            className="max-w-full max-h-[70vh] object-contain rounded-lg pointer-events-none"
            draggable={false}
          />
          {isPinMode && (
            <button
              type="button"
              aria-label="Place pin on image"
              onClick={handleImageClick}
              className="absolute inset-0 z-20 block h-full w-full cursor-crosshair border-0 bg-transparent p-0 touch-manipulation"
            />
          )}
          {/* Pin markers */}
          {pins.map((pin, idx) => (
            <button
              key={pin.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); selectPin(pin) }}
              className={`absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full flex items-center justify-center text-white text-[10px] font-bold border-2 border-white shadow-lg transition-transform ${
                selectedPin === pin.id ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'
              }`}
              style={{
                left: `${pin.x * 100}%`,
                top: `${pin.y * 100}%`,
                backgroundColor: severityColors[pin.severity] || '#6b7280',
              }}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Pin detail / comment panel */}
      {selectedPin && (
        <div className="bg-white rounded-t-2xl px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500">
              Pin {pins.findIndex((p) => p.id === selectedPin) + 1}
            </span>
            <button
              type="button"
              onClick={() => deletePin(selectedPin)}
              className="text-red-400 hover:text-red-600"
            >
              <Trash2 size={14} />
            </button>
          </div>

          <textarea
            placeholder="Describe the issue at this location…"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value)
              setValidationError(null)
            }}
            rows={2}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm resize-none focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            autoFocus
          />

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Severity:</span>
            {([1, 2, 3] as const).map((s) => {
              const labels = { 1: 'Low', 2: 'Mod', 3: 'High' } as const
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSeverity(s)}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                    severity === s
                      ? s === 3 ? 'bg-red-500 text-white' : s === 2 ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {labels[s]}
                </button>
              )
            })}
            <button
              type="button"
              onClick={updateSelectedPin}
              className="px-4 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500 text-white"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Pin list (when no pin selected) */}
      {!selectedPin && pins.length > 0 && (
        <div className="bg-white/95 rounded-t-2xl px-4 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] max-h-32 overflow-y-auto">
          {pins.map((pin, idx) => (
            <button
              key={pin.id}
              type="button"
              onClick={() => selectPin(pin)}
              className="w-full flex items-center gap-2 py-1.5 text-left text-xs"
            >
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                style={{ backgroundColor: severityColors[pin.severity] || '#6b7280' }}
              >
                {idx + 1}
              </span>
              <span className="text-slate-700 truncate flex-1">
                {pin.comment || 'No comment yet — tap to add'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
